/**
 * Friendly units (W1-03 core-model-ops; DESIGN.md §6.9 property catalogue, rpe-format.md F§4, F§7.1, F§9.1).
 *
 * Raw = the value written to RPE. Friendly = what the inspector shows:
 *  - Opacity: % = raw / 2.55 (raw 255 = 100 %);
 *  - Length (scaleX of normal lines): % = raw * 50 (Phira draws scaleX * 0.5 of the full length: raw 2.0 = 100 %);
 *  - Image / text / UI size: % = raw * 100;
 *  - Brush size of paint lines (R§3.7: the brush circle is drawn under the line scale, x0.5 rule included): width
 *    (scaleX) uses the Length conversion (raw 2.0 = 100 % = the absent track), height (scaleY) the size conversion
 *    (raw 1.0 = 100 %), so 100 % always means "the brush as Phira draws it without scale keys";
 *  - GIF progress: % = raw * 100;
 *  - position (RPE px), rotation and incline (degrees), speed (RPE units), thickness (multiplier), brush (device px):
 *    shown raw.
 * Conversions use the exact rational forms (raw * 100 / 255, not raw / 2.55) so whole percentages map to whole raw
 * values without binary noise (100 % -> 255, not 254.99999999999997).
 */

import type { FriendlyUnit, LineProp, PropUnitsApi, UnitContext } from './types';

/** Phira's SPEED_RATIO = 10 / 45 / HEIGHT_RATIO (F§7.1): floor height units per second at speed 1. */
export const SPEED_RATIO = 10 / 45 / 0.83175;
/** One floor-height unit in RPE-y px (half the 900 px screen). */
export const RPE_Y_PER_HEIGHT = 450;
/** RPE canvas size. */
export const RPE_WIDTH = 1350;
export const RPE_HEIGHT = 900;
/** Normal-line thickness in chart units when scaleY is absent or within 1e-4 of 1 (F§9.1). */
export const LINE_THICKNESS_DEFAULT = 0.01;
/** Normal-line thickness per scaleY unit otherwise (F§9.1). */
export const LINE_THICKNESS_PER_SCALE = 0.0076;
/** Chart units -> RPE-x px (1 chart unit = 675 RPE-x px). */
const RPE_X_PER_UNIT = RPE_WIDTH / 2;

function unitFor(prop: LineProp, ctx: UnitContext): FriendlyUnit {
  switch (prop) {
    case 'moveX':
    case 'moveY':
      return 'px';
    case 'rotate':
    case 'incline':
      return 'deg';
    case 'alpha':
      return 'opacityPercent';
    case 'speed':
      return 'speed';
    case 'scaleX':
    case 'scaleY':
      if (ctx.uiMover) return 'sizePercent';
      switch (ctx.kind) {
        case 'normal':
          return prop === 'scaleX' ? 'lengthPercent' : 'thickness';
        case 'texture':
        case 'gif':
        case 'text':
          return 'sizePercent';
        case 'paint':
          // Brush width / height in % of the unscaled brush (see the file header).
          return prop === 'scaleX' ? 'lengthPercent' : 'sizePercent';
      }
      return 'raw';
    case 'paint':
      return 'brushPx';
    case 'gif':
      return 'progressPercent';
    case 'color':
    case 'text':
      return 'raw';
  }
}

function toFriendlyUnit(unit: FriendlyUnit, raw: number): number {
  switch (unit) {
    case 'opacityPercent':
      return (raw * 100) / 255;
    case 'lengthPercent':
      return raw * 50;
    case 'sizePercent':
    case 'progressPercent':
      return raw * 100;
    case 'px':
    case 'deg':
    case 'speed':
    case 'thickness':
    case 'brushPx':
    case 'raw':
      return raw;
  }
}

function fromFriendlyUnit(unit: FriendlyUnit, friendly: number): number {
  switch (unit) {
    case 'opacityPercent':
      return (friendly * 255) / 100;
    case 'lengthPercent':
      return friendly / 50;
    case 'sizePercent':
    case 'progressPercent':
      return friendly / 100;
    case 'px':
    case 'deg':
    case 'speed':
    case 'thickness':
    case 'brushPx':
    case 'raw':
      return friendly;
  }
}

function crossScreenSec(speed: number): number {
  if (!(speed > 0)) return Number.POSITIVE_INFINITY;
  // The 900 px screen is 2 floor-height units; the floor advances speed * SPEED_RATIO units per second.
  return RPE_HEIGHT / RPE_Y_PER_HEIGHT / (speed * SPEED_RATIO);
}

function thicknessPx(scaleY: number | null): number {
  if (scaleY === null || Math.abs(scaleY - 1) <= 1e-4) return LINE_THICKNESS_DEFAULT * RPE_X_PER_UNIT;
  return Math.abs(LINE_THICKNESS_PER_SCALE * scaleY) * RPE_X_PER_UNIT;
}

export const Units: PropUnitsApi = {
  unitFor,
  toFriendly: (prop, raw, ctx) => toFriendlyUnit(unitFor(prop, ctx), raw),
  fromFriendly: (prop, friendly, ctx) => fromFriendlyUnit(unitFor(prop, ctx), friendly),
  crossScreenSec,
  thicknessPx,
};

/** Raw -> friendly for an explicit unit (inspector fields with a units toggle). */
export function toFriendly(unit: FriendlyUnit, raw: number): number {
  return toFriendlyUnit(unit, raw);
}

/** Friendly -> raw for an explicit unit. */
export function fromFriendly(unit: FriendlyUnit, friendly: number): number {
  return fromFriendlyUnit(unit, friendly);
}

/** Note scroll speed in RPE-y px per second for a line speed value (speed 1 = 120.23 px/s). */
export function speedPxPerSec(speed: number): number {
  return speed * SPEED_RATIO * RPE_Y_PER_HEIGHT;
}

/**
 * Line speed that crosses the 900 px screen in `sec` seconds (inverse of `crossScreenSec`); Infinity for sec <= 0.
 */
export function speedForCrossSec(sec: number): number {
  if (!(sec > 0)) return Number.POSITIVE_INFINITY;
  return RPE_HEIGHT / RPE_Y_PER_HEIGHT / (sec * SPEED_RATIO);
}

/**
 * Thickness of a normal line in screen px for a viewport width (the inspector's "actual px" helper): RPE-x px scale
 * with the viewport, 1350 RPE-x px = the full width.
 */
export function thicknessScreenPx(scaleY: number | null, viewportW: number): number {
  return (thicknessPx(scaleY) * viewportW) / RPE_WIDTH;
}
